import { useEffect } from 'react';

/**
 * Custom hook to prevent zoom gestures on UI elements while allowing them on map
 * Handles touch, gesture, wheel, and pointer events to prevent accidental zoom
 */
export function usePreventZoom() {
  useEffect(() => {
    // Helper function to check if event target is within map container
    const isMapElement = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof Element)) return false;

      // Check if element is part of MapLibre or map container
      return !!(
        target.closest('.maplibregl-map') ||
        target.closest('.maplibregl-canvas') ||
        target.closest('[class*="mapSection"]') ||
        target.closest('[data-testid="map-container"]') ||
        target.classList.contains('maplibregl-canvas') ||
        target.classList.contains('maplibregl-map') ||
        target.classList.contains('maplibregl-control-container') ||
        // Check parent elements up the tree
        target.parentElement?.classList.contains('maplibregl-map') ||
        target.parentElement?.classList.contains('maplibregl-canvas')
      );
    };

    // Prevent multi-touch gestures (pinch zoom) from starting on UI elements
    const handleTouchStart = (e: TouchEvent) => {
      // Allow multi-touch on map elements
      if (isMapElement(e.target)) return;

      // If more than one finger is touching UI elements, prevent the gesture
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Allow multi-touch on map elements
      if (isMapElement(e.target)) return;

      // Prevent multi-touch during move on UI elements
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const handleGestureStart = (e: Event) => {
      // Allow gestures on map elements
      if (isMapElement(e.target)) return;

      e.preventDefault();
    };

    const handleGestureChange = (e: Event) => {
      // Allow gestures on map elements
      if (isMapElement(e.target)) return;

      e.preventDefault();
    };

    const handleGestureEnd = (e: Event) => {
      // Allow gestures on map elements
      if (isMapElement(e.target)) return;

      e.preventDefault();
    };

    // Prevent double-tap zoom on iOS for UI elements
    let lastTouchEnd = 0;
    const handleTouchEnd = (e: TouchEvent) => {
      // Allow double-tap on map elements
      if (isMapElement(e.target)) return;

      const now = new Date().getTime();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    // Prevent wheel zoom on UI elements (for Chrome - more aggressive)
    const handleWheel = (e: WheelEvent): void => {
      // Allow wheel zoom on map elements
      if (isMapElement(e.target)) return;

      // Prevent zoom with Ctrl+scroll, pinch on trackpad, or any deltaY with ctrlKey
      if (e.ctrlKey || e.metaKey || (Math.abs(e.deltaY) > 0 && e.ctrlKey)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Chrome-specific pointer event handling
    const activePointers = new Set<number>();

    const handlePointerDown = (e: PointerEvent): void => {
      // Allow pointer events on map elements
      if (isMapElement(e.target)) return;

      // Track active pointers
      activePointers.add(e.pointerId);

      // If more than one pointer (multi-touch), prevent zoom
      if (activePointers.size > 1 && e.pointerType === 'touch') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handlePointerUp = (e: PointerEvent): void => {
      // Remove pointer from tracking
      activePointers.delete(e.pointerId);
    };

    const handlePointerCancel = (e: PointerEvent): void => {
      // Remove pointer from tracking
      activePointers.delete(e.pointerId);
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('gesturestart', handleGestureStart, { passive: false });
    document.addEventListener('gesturechange', handleGestureChange, { passive: false });
    document.addEventListener('gestureend', handleGestureEnd, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
    document.addEventListener('wheel', handleWheel, { passive: false });

    // Chrome-specific pointer events
    document.addEventListener('pointerdown', handlePointerDown, { passive: false });
    document.addEventListener('pointerup', handlePointerUp, { passive: false });
    document.addEventListener('pointercancel', handlePointerCancel, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('gesturestart', handleGestureStart);
      document.removeEventListener('gesturechange', handleGestureChange);
      document.removeEventListener('gestureend', handleGestureEnd);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, []);
}